import { graphql } from "gatsby"
import React from "react"
import styled, { css } from "styled-components"
import Breakpoints from "../../../components/shared/breakpoints"
import Grid from "../../atoms/layout-primitives/grid"
import VariantContainer, { IStyleVariant } from "../../atoms/variant-container"
import FeatureCard, {
  IFeature,
} from "../../molecules/feature-card/feature-card"
import { BackgroundColourContextProvider } from "../../util/background-colour-context"
import mergeVariants from "../../util/merge-variants"
import withAnimation from "../../util/withAnimation"
import FeatureGridCol from "./feature-grid-col"
import FeatureGridRow from "./feature-grid-row"

const GridContainer = styled(VariantContainer)``

const BaseStyle = css`
  display: grid;
  grid-template-columns: 1fr;
  grid-column: 1 / -1;
  grid-row-gap: 32px;
  width: 100%;

  @media (min-width: ${Breakpoints.md}) {
    grid-column: 2 / -2;
    grid-row-gap: 48px;
  }
  @media (min-width: ${Breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    grid-row-gap: 64px;
    grid-column-gap: 96px;
  }

  @media (min-width: ${Breakpoints.xl}) {
    grid-column: 3 / -3;
  }
`
interface IFeatureGridContainer {
  items: IFeature[]
  className?: string
  containerVariants?: IStyleVariant | any[]
  titleVariants?: IStyleVariant | any[]
  textVariants?: IStyleVariant | any[]
  dark?: boolean
}

const FeatureGridContainer = ({
  items,
  className,
  dark,
  containerVariants,
  titleVariants,
  textVariants,
}: IFeatureGridContainer) => {
  const CardType = dark ? FeatureCard.DarkText : FeatureCard
  return (
    <GridContainer
      className={className}
      variants={mergeVariants([BaseStyle], containerVariants)}
    >
      {items.map((item) => (
        <CardType
          key={item.title}
          featureDetails={item}
          titleVariants={titleVariants}
          textVariants={textVariants}
        />
      ))}
    </GridContainer>
  )
}

FeatureGridContainer.Animated = withAnimation(FeatureGridContainer)

export interface IContentfulFeatureGrid {
  __typename: "ContentfulFeatureGrid"
  features: any[]
  backgroundColour: {
    hex: string
  }
}

export const ContentfulFeatureGrid = ({
  features,
  backgroundColour,
}: Omit<IContentfulFeatureGrid, "__typename">) => {
  if (!Array.isArray(features)) {
    return null
  }

  return (
    <BackgroundColourContextProvider colour={backgroundColour?.hex}>
      <Grid>
        {Array.isArray(features) && features.length <= 3 ? (
          <FeatureGridRow features={features} />
        ) : (
          <FeatureGridCol features={features} />
        )}
      </Grid>
    </BackgroundColourContextProvider>
  )
}

export const FeatureGridFragment = graphql`
  fragment ContentfulFeatureGrid on ContentfulFeatureGrid {
    contentful_id
    features {
      ... on ContentfulContentBlockWithIcon {
        ...ContentfulContentBlockWithIcon
      }
    }
    backgroundColour {
      hex
    }
  }
`
export const FEATURE_GRID_TYPENAME = "ContentfulFeatureGrid"
export default styled(FeatureGridContainer)``
